/**
 * Turn Color Parser
 *
 * SINGLE SOURCE OF TRUTH for Turn Color parsing logic.
 * All components MUST import from here, not create their own parsers.
 *
 * Turn Code format:
 * - Plain letters (e.g. `h`, `t`) are consonants rendered without color.
 * - Vowel g-p values (vowel grapheme-phoneme) are written as `[grapheme-color]`
 *   and encode the relationship between a grapheme (the letter or letter-cluster)
 *   and its phoneme (represented by a color name). Example: `[e-green]` means
 *   the grapheme 'e' maps to the Long-E phoneme (green).
 * - Commas separate segments and are ignored during rendering.
 *
 * Example: "h, [e-green], ll, [o-yellow]" for the word "hello"
 */

export interface TurnColorSegment {
  type: 'text' | 'color';
  content: string;
  color?: string;
}

export interface TurnColorMapping {
  id?: string;
  color_name: string;
  hex_code: string;
  description?: string;
}

/**
 * Parse a turn code string into renderable segments.
 *
 * Plain-text segments are consonants. Segments with `type: 'color'` are
 * vowel g-p values — each carries a grapheme (`content`) and the color
 * that represents its phoneme (`color`).
 *
 * @param turnCode - The turn code string (e.g., "h, [e-green], ll, [o-yellow]")
 * @returns Array of segments with type, content, and optional color
 *
 * @example
 * parseTurnCode("h, [e-green]")
 * // Returns: [
 * //   { type: 'text',  content: 'h' },
 * //   { type: 'color', content: 'e', color: 'green' }  // ← vowel g-p value
 * // ]
 */
export function parseTurnCode(turnCode: string): TurnColorSegment[] {
  if (!turnCode) return [];

  const segments: TurnColorSegment[] = [];
  let currentIndex = 0;
  let currentText = '';

  while (currentIndex < turnCode.length) {
    const char = turnCode[currentIndex];

    if (char === '[') {
      // Save any accumulated text
      if (currentText.trim()) {
        segments.push({
          type: 'text',
          content: currentText.trim()
        });
        currentText = '';
      }

      // Find the closing bracket
      const closingIndex = turnCode.indexOf(']', currentIndex);
      if (closingIndex === -1) {
        // No closing bracket, treat as regular text
        currentText += char;
        currentIndex++;
        continue;
      }

      // Extract the content inside brackets
      const bracketContent = turnCode.substring(currentIndex + 1, closingIndex);
      const [content, color] = parseBracketContent(bracketContent);

      segments.push({
        type: 'color',
        content: content || '',
        color: color || ''
      });

      currentIndex = closingIndex + 1;
    } else if (char === ',') {
      // Handle comma as separator
      if (currentText.trim()) {
        segments.push({
          type: 'text',
          content: currentText.trim()
        });
        currentText = '';
      }
      currentIndex++;
    } else {
      currentText += char;
      currentIndex++;
    }
  }

  // Add any remaining text
  if (currentText.trim()) {
    segments.push({
      type: 'text',
      content: currentText.trim()
    });
  }

  return segments.filter(segment => segment.content);
}

/**
 * Split a vowel g-p value's inner content into [grapheme, phoneme-color].
 * e.g. "e-green" → ["e", "green"],  "ea-green" → ["ea", "green"]
 */
function parseBracketContent(content: string): [string, string] {
  const parts = content.split('-');
  if (parts.length === 1) {
    return [parts[0], parts[0]]; // If no color specified, use content as color
  }
  return [parts[0], parts.slice(1).join('-')];
}

/**
 * Get hex color for a color name from mappings
 * 
 * @param colorName - The color name (e.g., "green", "red")
 * @param colorMappings - Array of color mappings from database
 * @returns Hex color code (e.g., "#00FF00") or default gray
 */
export function getHexColor(colorName: string, colorMappings: TurnColorMapping[]): string {
  const mapping = colorMappings.find(c => 
    c.color_name && c.color_name.toLowerCase() === colorName.toLowerCase()
  );
  return mapping?.hex_code || '#999999'; // Default to gray if not found
}

/**
 * Check if a color is enabled in the current filter set
 * 
 * @param colorName - The color name to check
 * @param enabledColors - Set of enabled color names, or undefined for all enabled
 * @returns true if the color should be rendered with Turn Color styling
 */
export function isColorEnabled(colorName: string, enabledColors?: Set<string>): boolean {
  if (!enabledColors) return true; // All colors enabled if no filter
  return enabledColors.has(colorName.toLowerCase());
}

/**
 * Validate a turn code format
 * 
 * @param turnCode - The turn code string to validate
 * @returns Object with isValid boolean and array of error messages
 */
export function validateTurnCode(turnCode: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!turnCode) {
    errors.push('Turn code cannot be empty');
    return { isValid: false, errors };
  }

  // Check for balanced brackets
  const openBrackets = (turnCode.match(/\[/g) || []).length;
  const closeBrackets = (turnCode.match(/\]/g) || []).length;
  
  if (openBrackets !== closeBrackets) {
    errors.push('Unbalanced brackets in turn code');
  }

  // Check for empty brackets
  if (turnCode.includes('[]')) {
    errors.push('Empty brackets are not allowed');
  }

  // Check for malformed bracket content
  const bracketRegex = /\[([^\]]*)\]/g;
  let match;
  while ((match = bracketRegex.exec(turnCode)) !== null) {
    const content = match[1];
    if (!content || content.trim() === '') {
      errors.push('Brackets must contain content');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Reconstruct word text from turn code segments
 * 
 * @param segments - Array of turn code segments
 * @returns Plain text word
 */
export function segmentsToText(segments: TurnColorSegment[]): string {
  return segments.map(s => s.content).join('');
}

/**
 * Standard color palette used by Turn Color system (14 colors)
 * 
 * CANONICAL SOURCE: This should match the database `turn_color_mappings` table.
 * Each color represents a specific phoneme category in the Turn reading system.
 */
export const STANDARD_COLORS: TurnColorMapping[] = [
  // CONSONANTS
  { color_name: 'black', hex_code: '#000000', description: 'Default consonants' },
  
  // VOWELS - Short Monophthongs
  { color_name: 'red', hex_code: '#E53935', description: 'Short E /ɛ/ (bed)' },
  { color_name: 'pink', hex_code: '#FFB7CE', description: 'Short I /ɪ/ (bit)' },
  { color_name: 'plum', hex_code: '#8E24AA', description: 'Short U /ʌ/ (cut)' },
  
  // VOWELS - Long Monophthongs
  { color_name: 'green', hex_code: '#43A047', description: 'Long E /iː/ (beat)' },
  { color_name: 'blue', hex_code: '#1E88E5', description: 'Long U /uː/ (boot)' },
  { color_name: 'aqua', hex_code: '#00ACC1', description: 'AW sound /ɔː/ (saw, caught)' },
  { color_name: 'wood', hex_code: '#8D6E63', description: 'BOOK vowel /ʊ/ (put)' },
  
  // VOWELS - Diphthongs
  { color_name: 'yellow', hex_code: '#FDD835', description: 'Long O /oʊ/ (coat)' },
  { color_name: 'lime', hex_code: '#7CB342', description: 'Long I /aɪ/ (kite)' },
  { color_name: 'brown', hex_code: '#6D4C41', description: 'OW sound /aʊ/ (cow)' },
  
  // R-CONTROLLED & SPECIAL
  { color_name: 'orange', hex_code: '#FB8C00', description: 'R-controlled vowels' },
  { color_name: 'purple', hex_code: '#7B1FA2', description: 'Special sounds' },
  
  // UTILITY
  { color_name: 'gray', hex_code: '#757575', description: 'Unknown/default' },
];

